import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import type { SnakeCasedPropertiesDeep } from 'type-fest';

import { keysToCamelCase, keysToSnakeCase } from '@hotjar/webapp/lib/convert-string';

import type {
  AccountType,
  BillingCycle,
  Currency,
  Features,
  PaymentMethods,
  RegistrationCampaign,
  TrialState,
} from './model';

export interface OwnedAccount {
  address: string | null;
  becameACustomer: number | null;
  billingCycle: BillingCycle;
  billingVersion: number;
  cardExpirationDate: number | null;
  company: string | null;
  created: number;
  currency: Currency;
  features: Features;
  hasPaidPlan: boolean;
  hasPaymentDetails: boolean;
  highestPlan: FeatureGroup;
  id: number;
  isCustomer: boolean;
  location: string | null;
  lock: boolean;
  nextBillingDate: number | null;
  paymentMethodType: PaymentMethods | null;
  percentageDiscount: number | null;
  pricingTableId: string;
  recurringPaymentFailed: boolean;
  registrationCampaign: RegistrationCampaign | null;
  trialState: TrialState | null;
  type: AccountType;
  unpaidInvoice: boolean | null;
  vatNumber: string | null;
}

export type OwnedAccountDto = SnakeCasedPropertiesDeep<OwnedAccount>;

export function mockOwnedAccountDto(overrides: Partial<OwnedAccountDto> = {}): OwnedAccountDto {
  return {
    address: null,
    became_a_customer: 1618387097,
    billing_cycle: 'yearly_recurring',
    billing_version: 2,
    card_expiration_date: 1838073600,
    company: 'Test Company',
    created: 1618365278,
    currency: 'eur',
    features: ['poll.poll_response_filters'],
    has_paid_plan: true,
    has_payment_details: true,
    highest_plan: 'business',
    id: 1,
    is_customer: true,
    location: 'EU,MT',
    lock: false,
    next_billing_date: 1649894400,
    payment_method_type: 'credit_card',
    percentage_discount: 0,
    pricing_table_id: '3',
    recurring_payment_failed: false,
    registration_campaign: null,
    trial_state: null,
    type: 'agency',
    unpaid_invoice: null,
    vat_number: null,
    ...overrides,
  };
}

export const mapOwnedAccountModelToDto = (account: OwnedAccount): OwnedAccountDto =>
  keysToSnakeCase(account);

export const mapOwnedAccountDtoToModel = (account: OwnedAccountDto): OwnedAccount =>
  keysToCamelCase(account);

export function mockOwnedAccount(overrides: Partial<OwnedAccount> = {}): OwnedAccount {
  const usersAccountDto = mockOwnedAccountDto(keysToSnakeCase(overrides));

  return mapOwnedAccountDtoToModel(usersAccountDto);
}
